import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { toast } from 'react-toastify';
import { authHeader, config } from '../../_helpers';
import { formatDate } from '_helpers/helpers';

const ApplicantRightColumnSection = ({ applicants, checkProperties }) => {
  const [downloading, setDownloading] = useState(false);

  const formatAnswer = (val) =>
    val && val !== 'null' && val !== '' ? (
      val
    ) : (
      <span className='av-empty'>Did not fill out</span>
    );

  const fileDownload = async () => {
    const hash = applicants?.data?.hash;

    if (!hash) {
      toast.error('Unable to download: Applicant data not found');
      return;
    }

    setDownloading(true);
    try {
      const response = await fetch(
        `${config.apiUrl}/api/v1/applicant/${hash}/download-files`,
        {
          method: 'GET',
          headers: authHeader(),
        },
      );

      if (!response.ok) {
        const errorData = await response.json();
        toast.error(errorData.message || 'Download failed');
        return;
      }

      // Get the filename from the Content-Disposition header if available
      const contentDisposition = response.headers.get('Content-Disposition');
      let filename = 'qualification-files.zip';
      if (contentDisposition) {
        const match = contentDisposition.match(/filename="?(.+)"?/);
        if (match) {
          filename = match[1];
        }
      }

      // Create blob and download
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      toast.success('Files downloaded successfully');
    } catch (error) {
      console.error('Download failed:', error);
      toast.error('Download failed. Please try again.');
    } finally {
      setDownloading(false);
    }
  };

  const renderApplyingPosition = () => {
    const positions = applicants?.data?.applying_position || [];
    return positions.length ? (
      positions.map((pos) => (
        <p key={pos} className='av-answer av-answer-clear'>
          {pos}
        </p>
      ))
    ) : (
      <span className='av-empty'>Did not fill out</span>
    );
  };

  const renderQualifications = () => {
    const quals = applicants?.data?.qualification || [];
    return quals.length ? (
      quals.map((qual, index) => (
        <p key={index} className='av-answer av-answer-clear'>
          {qual}
        </p>
      ))
    ) : (
      <span className='av-empty'>Did not fill out</span>
    );
  };

  const renderLicense = () => {
    if (checkProperties(applicants.data.license)) {
      return (
        <p key='license' id='avLicense' className='av-answer av-answer-clear'>
          <span className='av-empty'>Did not fill out</span>
        </p>
      );
    } else {
      const license = applicants.data.license;
      return (
        <div key='license' id='avLicense' className='av-answer av-answer-clear'>
          {license.state_id && license.state_id !== 'null' && (
            <p className='av-empty'>{license.state_id}</p>
          )}
          {license.no && license.no !== 'null' && (
            <p className='av-empty'>{license.no}</p>
          )}
          {license.exp_date && license.exp_date !== 'null' && (
            <p className='av-empty'>{formatDate(license.exp_date)}</p>
          )}
          {license.class && license.class !== 'null' && (
            <p className='av-empty'>{license.class}</p>
          )}
          {license.endorsement && license.endorsement !== 'null' && (
            <p className='av-empty'>{license.endorsement}</p>
          )}
        </div>
      );
    }
  };

  return (
    <div className='right col-50 applicant-view-right'>
      <h4 className='av-title'>Availability and Experience</h4>

      <p className='av-question'>Current Certification Status:</p>
      <p id='avCertStatus' className='av-answer av-answer-clear'>
        {formatAnswer(applicants?.data?.certification_status)}
      </p>
      <br />

      {applicants?.data?.general_qualification && (
        <>
          <p className='av-question'>Qualification:</p>
          <p id='avGenQualification' className='av-answer av-answer-clear'>
            {applicants.data.general_qualification}
          </p>
          <br />
        </>
      )}

      <p className='av-question'>
        Anticipated Living Location for Fire Season:
      </p>
      <p id='avAnticipatedLoc' className='av-answer av-answer-clear'>
        {formatAnswer(applicants?.data?.anticipated_location)}
      </p>
      <br />

      <p className='av-question'>Anticipated relocation date:</p>
      <p id='avAnticipatedLocDate' className='av-answer av-answer-clear'>
        {applicants?.data?.anticipated_relocation ? (
          formatDate(applicants.data.anticipated_relocation)
        ) : (
          <span className='av-empty'>Did not fill out</span>
        )}
      </p>
      <br />

      <p className='av-question'>Desired Reporting Location:</p>
      <p id='avReportingLoc' className='av-answer av-answer-clear'>
        {formatAnswer(applicants?.data?.reportinglocation)}
      </p>
      <br />

      <p className='av-question'>Position Applying for:</p>
      <div id='avApplyingPosition' className='av-answer av-answer-clear'>
        {renderApplyingPosition()}
      </div>
      <br />

      <p className='av-question'>Position to be Considered:</p>
      <p id='avPositionOn' className='av-answer av-answer-clear'>
        {formatAnswer(applicants?.data?.position_on)}
      </p>
      <br />

      <p className='av-question'>Current Fire Qualifications:</p>
      <div id='avQualification' className='av-answer av-answer-clear'>
        {renderQualifications()}
      </div>
      <br />

      <p className='av-question'>Copies of Qualification / Certificates:</p>
      <div id='fileQualificatns' className='av-answer av-answer-clear'>
        {applicants?.success &&
        applicants.data.filequalification?.length > 0 ? (
          <>
            <p>{applicants.data.filequalification.length} attached files</p>
            <button
              className='button'
              type='button'
              onClick={fileDownload}
              disabled={downloading}
            >
              {downloading ? 'Downloading...' : 'Download attachments'}
            </button>
          </>
        ) : (
          <span className='av-empty'>Did not fill out</span>
        )}
      </div>
      <br />

      <h4 className='av-title'>Driving History</h4>

      <p className='av-question'>Driver's License</p>
      {renderLicense()}
      <br />

      <p className='av-question'>
        Have you ever been denied a license, permit or privilege to operate a
        motor vehicle?
      </p>
      <p id='avDeniedLicense' className='av-answer av-answer-clear'>
        {formatAnswer(applicants?.data?.denied_license)}
      </p>
      <br />

      <p className='av-question'>
        Has any license, permit or privilege ever been revoked?
      </p>
      <div id='avRevokedLicense' className='av-answer av-answer-clear'>
        {applicants?.data?.license_revoked &&
        applicants.data.license_revoked !== 'null' ? (
          <>
            <span>{applicants.data.license_revoked}</span>
            {applicants.data.reason_license_revoked && (
              <>
                <p>
                  <br />
                  <strong>Explain:</strong>
                </p>
                <p>{applicants.data.reason_license_revoked}</p>
              </>
            )}
          </>
        ) : (
          <span className='av-empty'>Did not fill out</span>
        )}
      </div>
      <br />
    </div>
  );
};

ApplicantRightColumnSection.propTypes = {
  applicants: PropTypes.object.isRequired,
};

export default ApplicantRightColumnSection;
